import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

const LAST_UPDATED = "April 2026";

/*
 * IMPORTANT — Somveil is a brand, product line, and platform owned and operated
 * by HMH Labz LLP (a Limited Liability Partnership registered under the
 * Limited Liability Partnership Act, 2008, India).  All references in these
 * documents to "Somveil, we, us, our" mean HMH Labz LLP acting through the
 * Somveil brand.  Legal entity, notice, and grievance references use the
 * parent LLP name so the documents are enforceable and correctly attributed.
 */

const PRIVACY_SECTIONS = [
  {
    h: "1. Who we are",
    p: [
      "Somveil (\"Somveil\", \"we\", \"us\", \"our\") is a brand, product, and health-information platform owned and operated by HMH Labz LLP — a Limited Liability Partnership incorporated in India under the Limited Liability Partnership Act, 2008. HMH Labz LLP is the legal data fiduciary / data controller for all personal data processed through the Somveil brand, including the somveil.in website, associated services, and the OSA Risk Quiz.",
      "Somveil is a health-information and referral platform legally classified under the Information Technology Act, 2000 as an intermediary and content service provider. We are not a medical device, a telemedicine provider, a diagnostic laboratory, or a healthcare establishment under the Clinical Establishments Act, 2010.",
      "This Privacy Policy describes how HMH Labz LLP, through its Somveil brand, collects, uses, stores, shares, and protects personal data across our Pan-Asia operations — including but not limited to India, Singapore, Malaysia, Indonesia, Thailand, the Philippines, Vietnam, Hong Kong, Japan, South Korea, Taiwan, and the United Arab Emirates — in compliance with the applicable data protection laws of each jurisdiction."
    ]
  },
  {
    h: "2. Applicable laws",
    p: [
      "HMH Labz LLP designs Somveil's privacy practices to meet or exceed the following regulations, as relevant to where you access the service:",
    ],
    list: [
      "India — Digital Personal Data Protection Act, 2023 (DPDPA)",
      "Singapore — Personal Data Protection Act 2012 (PDPA)",
      "Malaysia — Personal Data Protection Act 2010",
      "Thailand — Personal Data Protection Act B.E. 2562 (2019)",
      "Indonesia — Personal Data Protection Law 2022 (UU PDP)",
      "Philippines — Data Privacy Act of 2012 (RA 10173)",
      "Vietnam — Decree No. 13/2023/ND-CP on Personal Data Protection",
      "Hong Kong — Personal Data (Privacy) Ordinance (PDPO)",
      "Japan — Act on the Protection of Personal Information (APPI)",
      "South Korea — Personal Information Protection Act (PIPA)",
      "Taiwan — Personal Data Protection Act",
      "United Arab Emirates — Federal Decree-Law No. 45 of 2021 (PDPL)",
      "European Union residents — General Data Protection Regulation (GDPR) as a voluntary baseline"
    ]
  },
  {
    h: "3. Data we collect",
    p: [
      "HMH Labz LLP, via the Somveil platform, collects only the information needed to deliver the service. Categories include:"
    ],
    list: [
      "Quiz responses: your answers to the 20 OSA screening questions, including any optional demographic information (age band, biological sex, lifestyle).",
      "Contact information: name, email address, phone number, city, and home address (the last only when booking a sleep study).",
      "Account information (doctors only): email address, password (stored as a one-way bcrypt hash), professional credentials, and registration numbers.",
      "Booking metadata: preferred dates, notes, and the specialist you select.",
      "Technical data: IP address, device type, browser user-agent, and pages accessed — used for security, fraud prevention, and aggregate usage analytics.",
      "Cookies and local storage: strictly necessary cookies for authentication; a small set of analytics cookies for platform improvement. You may disable non-essential cookies via your browser settings."
    ]
  },
  {
    h: "4. Why we collect it",
    p: [
      "HMH Labz LLP uses personal data collected through Somveil only for the purposes listed below:"
    ],
    list: [
      "To score your OSA risk quiz and generate your plain-language report.",
      "To match you with verified sleep specialists in your city.",
      "To transmit your booking request to the specialist you explicitly select, so their team can contact you to confirm the home sleep study.",
      "To allow empaneled doctors to view, manage, and update the status of bookings assigned to them.",
      "To communicate service-related messages (booking confirmations, password resets, status updates).",
      "To comply with applicable laws, legal process, and requests from competent regulators.",
      "To improve the service — for example, refining the quiz or the specialist directory — using de-identified, aggregated analytics."
    ]
  },
  {
    h: "5. How we share data",
    p: [
      "HMH Labz LLP does not sell your personal data. Ever. We share identifiable data only in three narrow circumstances:"
    ],
    list: [
      "With the specialist you explicitly choose to book with — so their clinical team can contact you and schedule the home sleep study. They act as independent data controllers from that point onward for clinical purposes.",
      "With service providers HMH Labz LLP engages under contract to operate the Somveil platform (for example, cloud hosting, email delivery, analytics). These providers are bound by strict confidentiality and data-processing terms and may only use the data on our instructions.",
      "With regulators, courts, or law enforcement — only when compelled by a valid legal order, and only to the minimum extent necessary."
    ]
  },
  {
    h: "6. Storage, security, and retention",
    p: [
      "Your data is stored on encrypted servers hosted in regionally appropriate data centres (primarily Mumbai and Singapore) operated by major cloud providers under ISO 27001 and SOC 2 controls. Passwords are stored only as bcrypt hashes — no one at HMH Labz LLP can see your password.",
      "HMH Labz LLP retains quiz responses and booking records for as long as your account remains active plus seven (7) years, after which they are deleted or irreversibly anonymised. You may request deletion at any time (see Section 8). Booking records that a specialist has accepted may be retained by that specialist independently under their own medical-records retention obligations."
    ]
  },
  {
    h: "7. International transfers",
    p: [
      "Because Somveil operates across Asia, HMH Labz LLP may transfer your data to and process it in a country different from the one in which you reside. Where required by law, we rely on contractual safeguards (standard contractual clauses) or adequacy findings to ensure your data receives a comparable level of protection. Users in jurisdictions with localisation requirements (for example, Vietnam and Indonesia) will have their primary copy of data stored domestically."
    ]
  },
  {
    h: "8. Your rights",
    p: [
      "Regardless of where you access Somveil, you have the following rights over the personal data HMH Labz LLP holds about you:"
    ],
    list: [
      "Access — request a copy of the personal data we hold about you.",
      "Correction — ask us to correct inaccurate or incomplete data.",
      "Deletion — request erasure of your data, subject to narrow legal retention requirements.",
      "Portability — receive your data in a structured, machine-readable format.",
      "Withdraw consent — at any time, with no effect on processing carried out before the withdrawal.",
      "Object to processing — for legitimate-interest based processing.",
      "Lodge a complaint — with the data protection authority in your jurisdiction (for India, the Data Protection Board of India once established under the DPDPA)."
    ],
    after: "To exercise any right, email privacy@somveil.in. HMH Labz LLP responds within 30 days, or sooner where the applicable law requires it."
  },
  {
    h: "9. Children",
    p: [
      "Somveil is not intended for persons under the age of 18. HMH Labz LLP does not knowingly collect personal data from children. If you believe a child has submitted data, please contact privacy@somveil.in and we will delete it immediately."
    ]
  },
  {
    h: "10. Cookies",
    p: [
      "HMH Labz LLP uses strictly necessary cookies on Somveil (authentication, session integrity) and a small number of first-party analytics cookies that do not track you across other sites. We do not use advertising cookies or share data with advertising networks. You may block non-essential cookies through your browser."
    ]
  },
  {
    h: "11. Grievance Officer (India — DPDPA)",
    p: [
      "In accordance with the Digital Personal Data Protection Act, 2023 and the Information Technology (Intermediary Guidelines) Rules, 2011, the designated Grievance Officer for users in India is:"
    ],
    list: [
      "Name: The Data Protection Officer",
      "Entity: HMH Labz LLP (operating the Somveil brand)",
      "Email: grievance@somveil.in",
      "Response time: within 30 days of receipt of a valid complaint"
    ]
  },
  {
    h: "12. Changes to this policy",
    p: [
      "HMH Labz LLP may update this Privacy Policy from time to time to reflect changes in law or our practices. Material changes will be communicated via email (to registered users) and by a prominent notice on the service for at least 14 days before they take effect. The \"Last updated\" date at the top of this page reflects the current version."
    ]
  },
  {
    h: "13. Contact",
    p: [
      "HMH Labz LLP — operator of the Somveil brand · Registered office: Chennai, India · privacy@somveil.in · grievance@somveil.in · hello@somveil.in"
    ]
  }
];

const TERMS_SECTIONS = [
  {
    h: "1. Acceptance of these Terms",
    p: [
      "By accessing or using Somveil (the \"Service\"), including the OSA Risk Quiz, specialist directory, and booking facility, you agree to be bound by these Terms of Use (\"Terms\"). If you do not agree, you must not use the Service. These Terms constitute a legally binding agreement between you and HMH Labz LLP — a Limited Liability Partnership incorporated in India under the Limited Liability Partnership Act, 2008 — which owns and operates the Somveil brand and platform (referred to in these Terms as \"Somveil\", \"we\", \"us\", or \"our\")."
    ]
  },
  {
    h: "2. Nature of the service — please read carefully",
    p: [
      "Somveil, operated by HMH Labz LLP, is a health-information and referral platform. Somveil is NOT:"
    ],
    list: [
      "A medical device under any applicable medical-device regulation.",
      "A diagnostic service, a clinical laboratory, or a hospital.",
      "A telemedicine practitioner, a pharmacy, or a prescribing authority.",
      "A substitute for consultation with a qualified healthcare professional."
    ],
    after: "The OSA Risk Quiz is a screening tool derived from validated questionnaires (STOP-BANG, Epworth Sleepiness Scale). Its output is a risk assessment, not a medical diagnosis. A diagnosis of obstructive sleep apnea can only be made by a qualified sleep specialist, using an overnight sleep study (home or in-laboratory polysomnography). You must not rely on Somveil output to make, delay, or avoid any medical decision. If you have an urgent medical concern, contact emergency services immediately."
  },
  {
    h: "3. Eligibility",
    p: [
      "You must be at least 18 years of age and have the legal capacity to enter into a binding contract in your jurisdiction to use Somveil. By using the Service, you represent and warrant to HMH Labz LLP that you meet these requirements."
    ]
  },
  {
    h: "4. Accounts",
    p: [
      "Certain features — including the doctor dashboard — require a Somveil account. You are responsible for maintaining the confidentiality of your credentials and all activity under your account. You agree to notify HMH Labz LLP immediately at security@somveil.in of any unauthorised access."
    ]
  },
  {
    h: "5. Pan-Asia operations and applicable law",
    p: [
      "HMH Labz LLP, through the Somveil platform, serves users across India, Singapore, Malaysia, Indonesia, Thailand, the Philippines, Vietnam, Hong Kong, Japan, South Korea, Taiwan, the United Arab Emirates, and other jurisdictions. Where the laws of your jurisdiction impose additional consumer, health-information, or data-protection obligations — for example, telemedicine rules under the Telemedicine Practice Guidelines, 2020 (India), the Healthcare Services Act (Singapore), or the PDPA-aligned guidelines elsewhere — those obligations apply in addition to these Terms and prevail over any conflicting provision to the extent required."
    ]
  },
  {
    h: "6. Specialist directory and bookings",
    p: [
      "Specialists listed on Somveil are independent licensed practitioners. HMH Labz LLP verifies their credentials (medical council registration number) at the time of empanelment. HMH Labz LLP and Somveil do NOT:"
    ],
    list: [
      "Employ or supervise any listed specialist.",
      "Guarantee the quality, outcome, or appropriateness of any consultation, test, or treatment.",
      "Take part in the clinical decision-making process between you and the specialist.",
      "Handle clinical complaints (these must be raised with the specialist directly or with the relevant medical council)."
    ],
    after: "When you submit a booking, you form a direct relationship with the specialist you select. HMH Labz LLP, through Somveil, transmits your details as a facilitator only. The specialist's clinical team will contact you to confirm the home sleep study visit, conduct the test, and report the results."
  },
  {
    h: "7. Payments",
    p: [
      "Payment integration is in a staged rollout. Until the integrated gateway operated by HMH Labz LLP is fully live, booking fees are collected directly by the specialist or their authorised lab at the time the home-visit technician arrives. Pricing displayed on the Service is the prevailing indicative price for a standard home sleep study (Type III polygraphy) and may vary based on clinical requirements or additional consumables."
    ]
  },
  {
    h: "8. Acceptable use",
    p: [
      "You agree not to:"
    ],
    list: [
      "Submit false or misleading information.",
      "Reverse-engineer, scrape, or otherwise extract content from the Service in bulk.",
      "Attempt to access the accounts of other users.",
      "Use the Service in a way that violates applicable law or infringes the rights of any third party.",
      "Upload malicious code, or probe the Service for vulnerabilities other than through a responsible disclosure to security@somveil.in."
    ]
  },
  {
    h: "9. Intellectual property",
    p: [
      "All content on Somveil — including the quiz text, scoring model, articles, design system, code, the Somveil name, logo, and wordmark — is the property of HMH Labz LLP or its licensors. \"Somveil\" and the Somveil three-bar mark are unregistered / registered trademarks of HMH Labz LLP (as applicable). You are granted a limited, non-exclusive, non-transferable licence to access and use the Service for personal, non-commercial purposes. Republishing, reselling, or redistributing content requires the prior written consent of HMH Labz LLP."
    ]
  },
  {
    h: "10. Disclaimers",
    p: [
      "To the maximum extent permitted by law:"
    ],
    list: [
      "The Service is provided on an \"as is\" and \"as available\" basis with no warranties of any kind, express or implied.",
      "HMH Labz LLP does not warrant that Somveil will be uninterrupted, error-free, or free of harmful components.",
      "HMH Labz LLP does not warrant or endorse any specific specialist, test, treatment, outcome, or recommendation obtained through the Service.",
      "Reliance on any information provided by the Service, including quiz output, is at your sole risk."
    ]
  },
  {
    h: "11. Limitation of liability",
    p: [
      "To the maximum extent permitted by applicable law, HMH Labz LLP, its designated partners, employees, contractors, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages — including but not limited to loss of profit, data, or medical-decision reliance — arising out of or related to your use of the Service. The total aggregate liability of HMH Labz LLP for any claim, direct or otherwise, shall not exceed the amount you actually paid to HMH Labz LLP (through the Somveil platform) in the twelve (12) months preceding the event giving rise to the claim, or INR 10,000, whichever is higher."
    ]
  },
  {
    h: "12. Indemnification",
    p: [
      "You agree to indemnify and hold HMH Labz LLP (and its designated partners, employees, and affiliates operating the Somveil brand) harmless from any claim or demand (including reasonable legal fees) arising from your breach of these Terms, your violation of applicable law, or your infringement of any third-party right."
    ]
  },
  {
    h: "13. Termination",
    p: [
      "HMH Labz LLP may suspend or terminate your access to the Service at any time, with or without notice, for conduct that we believe violates these Terms or is harmful to other users or the Service. Upon termination, your right to use the Service ceases immediately."
    ]
  },
  {
    h: "14. Governing law and dispute resolution",
    p: [
      "These Terms are governed by the laws of India, without regard to conflict-of-law principles. The courts at Chennai, Tamil Nadu shall have exclusive jurisdiction over any dispute arising out of or in connection with these Terms or the operation of Somveil by HMH Labz LLP, subject to your non-waivable statutory rights in your country of residence. Nothing in this clause limits consumer-protection rights that apply under local Pan-Asia laws (including but not limited to the Consumer Protection Act, 2019 in India, the Consumer Protection (Fair Trading) Act in Singapore, and equivalent legislation in other jurisdictions)."
    ]
  },
  {
    h: "15. Changes to these Terms",
    p: [
      "HMH Labz LLP may revise these Terms from time to time. Material changes will be posted on this page and, for registered users, notified by email with at least 14 days' notice before they take effect. Your continued use of the Service after the effective date constitutes acceptance of the updated Terms."
    ]
  },
  {
    h: "16. Contact",
    p: [
      "HMH Labz LLP — operator of the Somveil brand · Registered office: Chennai, India · hello@somveil.in · legal@somveil.in"
    ]
  }
];

function Section({ h, p, list, after }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 500, color: "var(--navy)", letterSpacing: "-0.01em", margin: "0 0 14px" }}>{h}</h2>
      {p?.map((para) => (
        <p key={para.slice(0, 40)} style={{ fontSize: 15, lineHeight: 1.7, color: "var(--grey-text)", margin: "0 0 14px" }}>{para}</p>
      ))}
      {list?.length > 0 && (
        <ul style={{ margin: "8px 0 14px", paddingLeft: 22, fontSize: 15, lineHeight: 1.7, color: "var(--grey-text)" }}>
          {list.map((item) => <li key={item.slice(0, 60)} style={{ marginBottom: 6 }}>{item}</li>)}
        </ul>
      )}
      {after && <p style={{ fontSize: 15, lineHeight: 1.7, color: "var(--grey-text)", margin: 0 }}>{after}</p>}
    </div>
  );
}

export default function Legal({ kind }) {
  const isPrivacy = kind === "privacy";
  const title = isPrivacy ? "Privacy Policy" : "Terms of Use";
  const sections = isPrivacy ? PRIVACY_SECTIONS : TERMS_SECTIONS;
  const lead = isPrivacy
    ? "This Privacy Policy describes how HMH Labz LLP, through its Somveil brand, collects, uses, shares, stores, and protects your personal data. It is designed to comply with data-protection laws across every Pan-Asia jurisdiction in which Somveil operates."
    : "These Terms of Use govern your access to and use of Somveil, a brand and platform owned and operated by HMH Labz LLP. Please read them carefully — they include important disclaimers about the nature of the service, limitations of liability, and the governing law that applies across our Pan-Asia operations.";

  return (
    <div data-testid={`${kind}-page`}>
      <Nav theme="light" />
      <section style={{ background: "var(--navy)", color: "white", padding: "140px 0 60px", position: "relative", overflow: "hidden" }}>
        <div className="starfield" />
        <div className="container-x" style={{ position: "relative", zIndex: 2, maxWidth: 820 }}>
          <span className="eyebrow" style={{ color: "var(--mint)" }}>Legal · Somveil · A HMH Labz LLP brand</span>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(40px, 5vw, 72px)", lineHeight: 1.02, letterSpacing: "-0.03em", fontWeight: 400, margin: "16px 0 20px", color: "white" }} data-testid={`${kind}-title`}>
            {title}
          </h1>
          <p style={{ fontSize: 17, color: "rgba(255,255,255,0.72)", lineHeight: 1.55, maxWidth: 640, margin: 0 }}>{lead}</p>
          <div style={{ marginTop: 24, fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>
            Last updated · {LAST_UPDATED}
          </div>
        </div>
      </section>

      <section style={{ background: "var(--cream)", padding: "80px 0 60px", minHeight: "60vh" }}>
        <div className="container-x" style={{ maxWidth: 820 }}>
          <div style={{ background: "white", border: "var(--border)", borderRadius: "var(--radius-lg)", padding: "48px 48px 32px" }}>
            {sections.map((s) => <Section key={s.h} {...s} />)}

            <div style={{ marginTop: 40, padding: "20px 24px", background: "rgba(78,205,196,0.08)", borderLeft: "2px solid var(--mint)", borderRadius: "0 8px 8px 0" }}>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--navy)", margin: 0 }}>
                <strong style={{ fontWeight: 500 }}>Reminder:</strong> Somveil (operated by HMH Labz LLP) is a screening and referral service. It is not a medical device, a diagnostic provider, or a substitute for qualified clinical advice. In a medical emergency, contact your local emergency number immediately.
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 32 }}>
            <Link to="/" className="btn btn-ghost" data-testid="legal-home-btn">← Back to home</Link>
            <Link to={isPrivacy ? "/terms" : "/privacy"} className="btn btn-ghost" data-testid="legal-switch-btn">
              {isPrivacy ? "Read Terms of Use →" : "Read Privacy Policy →"}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
